import React, { useState, useEffect } from "react";


import {
  Flex,
  Button,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useToast,
  Radio,
  RadioGroup,
  Divider,
} from "@chakra-ui/react";
import { LINK } from "config/config";
import axios, { isAxiosError } from "axios";
import { useGroup } from "hooks/useGroup";
import { useAuth } from "hooks/useAuth";

// EmptyLinkReq의 기본값 설정 (LinkReq 인터페이스에 맞게)
const EmptyLinkReq: LinkReq = {
  AccessToken: "",
  BucketName: "", // 실제 BUCKET 값은 requestLinkUpdate에서 설정되므로 여기서는 빈값으로 시작
  S3FilePath: "",
  TypeURL: "",
  ExpireCount: -1, // -1은 무제한을 의미
  ExpireDate: null, // 초기값을 null로 설정 (string | null 타입에 맞게)
  AutoDelete: true, // 새로 생성 시 기본값 true
  LinkMode: "both", // ★★★ 새로 생성 시 기본값 "both" ★★★
};

// deepcopy 함수는 LinkReq와 Link 모두를 처리할 수 있도록 any 타입으로 인자를 받도록 변경
// 또는 특정 타입만 처리한다면 해당 타입으로 명시
const deepcopy = (obj: any): any => {
  if (obj === undefined || obj === null) {
    return JSON.parse(JSON.stringify(EmptyLinkReq)); // undefined나 null이면 EmptyLinkReq 복사
  }
  return JSON.parse(JSON.stringify(obj));
};

const nameCheck = (name: string): boolean => {
  var rg1 = /^[^\\/:\*\?"<>\|]+$/;
  var rg2 = /^\./;
  var rg3 = /^(nul|prn|con|lpt[0-9]|com[0-9])(\.|$)/i;
  var rg4 = /%/;
  return rg1.test(name) && !rg2.test(name) && !rg3.test(name) && !rg4.test(name);
};

const UpdateModal = ({
  status,
  link,
  sourceFile,
  onClose,
  contentType,
}: {
  status: boolean;
  link?: Link;
  sourceFile: string;
  onClose: () => void;
  contentType: "audio" | "image" | "video";
}): React.ReactElement => {
  const toast = useToast();

  const [minDate, setMinDate] = useState<string>("");
  const [maxDate, setMaxDate] = useState<string>("");

  const { selected: group } = useGroup();  

  // 2025.06.20 mspark - Link 생성 및 수정 요청 email 처리
  const { user } = useAuth();

  const [edittedLinkReq, setEdittedLinkReq] = useState<LinkReq>(EmptyLinkReq);

  // selectedValue 초기값도 EmptyLinkReq.LinkMode로 설정
  const [selectedValue, setSelectedValue] = useState(EmptyLinkReq.LinkMode);

  const closeModal = () => {
    // 모달을 닫을 때 상태를 초기화
    setEdittedLinkReq(deepcopy(EmptyLinkReq));
    setSelectedValue(EmptyLinkReq.LinkMode);
    onClose();
  };

  useEffect(() => {
    // 모달이 열릴 때만 초기화 로직 수행
    if (status) {
      if (link) {
        // 기존 링크 수정 모드: link 객체의 속성을 LinkReq에 맞게 직접 매핑
        const newLinkReq: LinkReq = {
          LinkSeq: link.LinkSeq, // Link.LinkSeq는 number이므로 직접 할당
          S3FilePath: link.SourcePath,
          TypeURL: link.Link.substring(LINK.PREFIX.length),
          ExpireCount: link.Count,
          ExpireDate: link.Expire || null, // link.Expire가 undefined일 경우 null로 설정
          AutoDelete: link.AutoDelete,
          LinkMode: link.LinkMode,
          BucketName: LINK.BUCKET, // 기본값 설정
          AccessToken: "", // 기본값 설정
        };
        setEdittedLinkReq(newLinkReq);
        setSelectedValue(newLinkReq.LinkMode);
      } else {
        // ★★★ 새로운 링크 생성 모드: EmptyLinkReq의 기본값으로 초기화 ★★★
        setEdittedLinkReq(deepcopy(EmptyLinkReq)); // EmptyLinkReq는 이미 LinkReq 타입
        setSelectedValue(EmptyLinkReq.LinkMode);
      }

      // 날짜 범위 설정
      const date = new Date();
      setMinDate(date.toISOString().substring(0, 10));
      const next = new Date(date.setMonth(date.getMonth() + 1));
      setMaxDate(next.toISOString().substring(0, 10));
    }
  }, [status, link]); // link가 변경될 때도 useEffect가 재실행되도록 의존성 추가

  /*========================================================================================================================*/

  const requestLinkUpdate = async () => {
    try {
      if (edittedLinkReq.TypeURL !== "" && !nameCheck(edittedLinkReq.TypeURL)) {
        throw new Error('잘못된 링크 경로입니다.\n % / : * ? " < > | 가 포함된 경로는 불가능합니다.');
      }

      const requestBody = deepcopy(edittedLinkReq); // 전송할 데이터를 복사

      requestBody.BucketName = LINK.BUCKET;
      requestBody.AccessToken = "";
      requestBody.S3FilePath = sourceFile;

      // ExpireDate 처리: null 또는 빈 문자열이면 제거, 아니면 ISO 형식으로 변환
      if (requestBody.ExpireDate === null || requestBody.ExpireDate === "") {
        delete requestBody.ExpireDate;
      } else if (requestBody.ExpireDate) {
        requestBody.ExpireDate = new Date(requestBody.ExpireDate).toISOString();
      }

      // ExpireCount 처리: 0이하이면 -1로 설정
      if (requestBody.ExpireCount === undefined || requestBody.ExpireCount <= 0) {
        requestBody.ExpireCount = -1;
      }
      
      // 2025.06.20 mspark - Link 생성 및 수정 요청 email 처리
      requestBody.Email = user?.email || user?.UserName;

      if (link) {
        console.log("Patch Link Mode: ", requestBody.LinkMode);
        const _ = await axios.patch(`${LINK.CREATE}/${encodeURIComponent(group)}`, requestBody);
      } else {
        console.log("Create Link Mode: ", requestBody.LinkMode);
        const _ = await axios.post(`${LINK.CREATE}/${encodeURIComponent(group)}`, requestBody);
      }
      closeModal();
      toast({
        title: "링크 갱신 완료",
        status: "success",
        isClosable: true,
        duration: 10000,
      });
    } catch (err) {
      closeModal();

      if (isAxiosError(err)) {
        const description = err.response?.data.message
          ? `${err.response?.status}: ${JSON.stringify(err.response?.data.message)}`
          : `${err.cause}`;

        toast({
          title: "링크 갱신 실패",
          description: description,
          status: "error",
          isClosable: true,
          duration: 10000,
        });
      } else {
        toast({
          title: "링크 갱신 실패",
          description: `${err}`,
          status: "error",
          isClosable: true,
          duration: 10000,
        });
      }
    }
  };

  /*========================================================================================================================*/

  const updateLinkCount = (value: number) => {
    const newLink = deepcopy(edittedLinkReq);
    newLink.ExpireCount = value > 0 ? value : -1;
    setEdittedLinkReq(newLink);
  };

  const updateLinkExpireDate = (value: string) => {
    const newLink = deepcopy(edittedLinkReq);
    newLink.ExpireDate = value;
    setEdittedLinkReq(newLink);
  };

  const updateLinkMode = (value: string) => {
    const newLink = deepcopy(edittedLinkReq);
    newLink.LinkMode = value;
    console.log("Link Mode (from updateLinkMode): ", value);
    setEdittedLinkReq(newLink);
  };

  const updateLinkAutoDelete = (value: boolean) => {
    const newLink = deepcopy(edittedLinkReq);
    newLink.AutoDelete = value;
    setEdittedLinkReq(newLink);
  };

  /*========================================================================================================================*/

  return (
    <Modal closeOnOverlayClick={false} isOpen={status} onClose={closeModal} size={"4xl"} isCentered>
      <ModalOverlay />
      <ModalContent>
        {link ? <ModalHeader>Edit Link</ModalHeader> : <ModalHeader>Create New Link</ModalHeader>}
        <ModalCloseButton />
        <ModalBody pb={6} justifyContent={"left"}>

          <Divider width="90%" my={2} />

          <Flex width={"90%"}>
            <RadioGroup
              onChange={(nextValue) => {
                setSelectedValue(nextValue);
                updateLinkMode(nextValue);
              }}
              value={selectedValue}
            >
              <Text flexShrink={0} width={"150px"}>
                타입 선택
              </Text>
              <Radio value='both' ml="10px">
                다운로드+{contentType === 'image' ? '프리뷰' : '스트리밍'}
              </Radio>
              <Radio value='download' ml="10px">
                다운로드
              </Radio>
              <Radio value='streaming' ml="10px">
                {contentType === 'image' ? '프리뷰' : '스트리밍'}
              </Radio>
            </RadioGroup>
          </Flex>

          <Divider width="90%" my={2} />

          <Flex width={"100%"} justify={"left"} align={"center"}>
            <Text flexShrink={0} width={"150px"}>
              유효기간 지정
            </Text>
            <Input
              width={"200px"}
              placeholder={"Select Date and Time"}
              type={"date"}
              min={minDate}
              max={maxDate}
              value={edittedLinkReq.ExpireDate || ""}
              onChange={(e) => updateLinkExpireDate(e.target.value)}
            />
          </Flex>
          <Text color={"gray"} size={"sm"} ml={"10px"}>
            오늘로부터 최대 한달까지 선택 가능
          </Text>

          <Divider width="90%" my={2} />
          
          <Flex width={"100%"} justify={"left"} align={"center"}
            visibility={selectedValue === 'both' || selectedValue === 'download' ? 'visible' : 'hidden'}>
            <Text flexShrink={0} width={"150px"}>
              다운로드 횟수 제한
            </Text>
            <NumberInput
              width={"200px"}
              min={1}
              max={10}
              keepWithinRange={true}
              clampValueOnBlur={false}
              allowMouseWheel={true}
              value={edittedLinkReq.ExpireCount === -1 ? 1 : edittedLinkReq.ExpireCount}
              onChange={(_, num) => updateLinkCount(num)}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <Text color={"gray"} size={"sm"} ml={"10px"}>
              (최솟(기본)값 1 / 최댓값 10)
            </Text>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button onClick={() => requestLinkUpdate()} colorScheme={"blue"} mr={3}>
            Apply
          </Button>
          <Button onClick={closeModal} colorScheme={"red"}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UpdateModal;